import * as schema from '@fullstory/client-api';
import { copyCriteria, maybeWrapValue, maybeUnwrapValue } from './criteria';
import {
  SearchType,
  SearchFilter,
  Kind,
  Subkind,
  EventType,
  DependentType,
  CustomErrorSubkinds,
} from '@fullstory/search-types';
import { DocumentCriterion } from '@fullstory/client-api';
import { DependentEventFilter, EventFilter } from '@fullstory/proto/fs/api/segment';
// ConverterSpec is the leaf type of the values in the converter maps.
// See EventConverterMap and DependentConverterMap, for example.
//
// TODO(nishanth): make the method names more clarifying.
export type ConverterSpec<T extends SearchType> = {
  // maps a search type to a filter. Performed via a direct lookup so not necessary to ever return null.
  searchTypeToFilter: (
    s: T,
    eventToFilter: (st: EventType) => EventFilter,
    dependentToFilter: (st: DependentType) => DependentEventFilter,
  ) => SearchFilter;
  // maps a generated searchfilter to a search type. Returns null if invalid path.
  filterToSearchType: (
    s: SearchFilter,
    filterToEvent: (st: EventFilter) => EventType,
    filterToDependent: (st: DependentEventFilter) => DependentType,
  ) => SearchType | null;
  // disabled determines if the conversion should be ignored.
  disabled?: (fl: schema.Flags) => boolean;
  // Infer appropriate search type based on a partial document criterion such as those sent from suggestive.
  withoutPartials: (s: T, withoutPartials: (st: T) => T | null) => { s: T; ok: boolean };

  /////////////////////////////////////////////////////////////////////////
  // DO NOT IMPLEMENT ON NEW SEARCH TYPES.
  // These are legacy converters to deal with DocumentCriterion.
  /////////////////////////////////////////////////////////////////////////
  // generate criteria for the given search type
  criteria?: (s: T) => schema.DocumentCriterion[];
  // extract the search type from given criteria. If the value could not
  // be extracted, the returned search type will be null.
  extractFromCriteria?: (c: schema.DocumentCriterion[]) => T | null;
  // extractFromPartialCriteria is a one way conversion from a document criterion to a partial search type -
  // inferring matches loosely off of unique fields on the criteria instead of an equality check.
  extractFromPartialCriteria?: (c: schema.DocumentCriterion[]) => T | null;
  /////////////////////////////////////////////////////////////////////////
};

// Some SearchTypes are client-only constructs, for example orgEvent
// ClientOnlyConverterSpec is a type that converts filters that do not exist on the client
// (e.g. "custom", "definedEvent") into the corresponding client-only filter
// The ConverterSpec for the client SearchType handles transforming the client-only type
// back into the valid proto type
export type ClientOnlyConverterSpec = {
  filterToSearchType: (sf: SearchFilter) => SearchType;
};

// withValueWrapping adapts a ConverterSpec to work with document criteria,
// which uses the value directly (i.e., does not use a single-element array)
// when a single value is present, but uses an array of values when multiple
// values are present.
//
// NOTE: this does not support value types such as [Date, Date] that are
// already arrays.
export function withValueWrapping<T extends SearchType>(
  c: ConverterSpec<T>,
  idx: number,
): ConverterSpec<T> {
  return {
    // when generating criteria, use the value directly if it's a single
    // value (i.e., represented by a single-element array).
    criteria: (s: T): schema.DocumentCriterion[] => {
      const criteria = c.criteria(s);
      // TODO(nishanth): stop wrapping when the old and new search UI's aren't
      // present concurrently in the app.
      criteria[idx].Value = maybeUnwrapValue(criteria[idx].Value);
      return criteria;
    },
    withoutPartials: c.withoutPartials,

    searchTypeToFilter: c.searchTypeToFilter,

    filterToSearchType: c.filterToSearchType,

    // when extracting, we wrap the criteria's value in an array if it's not already wrapped.
    extractFromCriteria: (criteria: schema.DocumentCriterion[]): T | null => {
      if (criteria[idx] == null) {
        return null;
      }
      criteria = copyCriteria(criteria);
      criteria[idx].Value = maybeWrapValue(criteria[idx].Value);
      return c.extractFromCriteria(criteria);
    },
    extractFromPartialCriteria: (criteria: schema.DocumentCriterion[]): T | null => {
      if (criteria[idx] == null) {
        return null;
      }
      criteria = copyCriteria(criteria);
      criteria[idx].Value = maybeWrapValue(criteria[idx].Value);
      return c.extractFromPartialCriteria(criteria);
    },
    disabled: c.disabled,
  };
}

export const FieldEventType = 'EventType';

export interface ParsedEvent {
  name: string;
  type: String;
  ok: boolean;
}

// Splits custom event in format evt_(name)_(type)
export function parseEvent(event: string): ParsedEvent {
  // Chomp off the scope. And use it if it looks like a valid scope.
  const firstUnderscore = event.indexOf('_');
  if (firstUnderscore === -1) {
    return { name: '', type: '', ok: false };
  }

  const scope = event.substring(0, firstUnderscore);
  if (scope !== 'evt') {
    return { name: '', type: '', ok: false };
  }

  const typedEvent = event.substring(firstUnderscore + 1);
  const lastUnderscore = typedEvent.lastIndexOf('_');
  if (lastUnderscore === -1) {
    return { name: '', type: '', ok: false };
  }

  return {
    name: typedEvent.substring(0, lastUnderscore),
    type: typedEvent.substring(lastUnderscore + 1),
    ok: true,
  };
}

export type DocumentCriteriaNullable = Array<DocumentCriterion | null> | null;
export type DocumentCriteriaList = Array<DocumentCriteriaNullable> | null;

export type ConverterMap<T extends SearchType> = {
  [K in Kind<T>]: SubkindConverterMap<T, K>;
};

export type SubkindConverterMap<T extends SearchType, K extends Kind<T>> = {
  [SK in Subkind<T, K>]: ConverterSpec<Extract<T, { kind: K; subkind: SK }>>;
};

export type ApiErrorClientConverter = {
  [SK in CustomErrorSubkinds]: ClientOnlyConverterSpec;
};
